import { Component, Inject, OnInit } from "@angular/core";
import { AbstractControl, FormControl, FormGroup, ValidatorFn, Validators } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogConfig, MatDialogRef } from "@angular/material/dialog";
import { ILayerStyleVector } from "../../viewer-components/interfaces/i-layer-style-vector";
import { Viewerlayer } from "../../viewer-components/model/viewerlayer";

@Component({
  selector: "lib-edit-layer-dialog",
  templateUrl: "./edit-layer-dialog.component.html",
  styleUrls: ["./edit-layer-dialog.component.scss"],
  providers: [MatDialogConfig],
  standalone: false,
})
export class EditLayerDialogComponent implements OnInit {
  colorFill;

  colorOutline;

  usedColors = [];

  vectorLayer = false;

  fields: string[];

  selectedField: string;

  additionalStyles: {
    field: string;
    filter: string[];
    paint: {
      fillColor: string;
      outlineColor: string;
    };
    editFillColor: boolean;
    editOutlineColor: boolean;
  }[] = [];

  editFillColor = false;

  editOutlineColor = false;

  infoBoxClosed = false;

  defaultFillColor = "rgba(49, 159, 211, 0.2)";

  defaultOutlineColor = "rgba(49,159,211,1)";

  formatPercentLabel = (value: number) => `${value}%`;

  defaultForm = new FormGroup({
    colorFill: new FormControl("rgba(49,159,211,0.2)"),
    colorOutline: new FormControl("rgba(49,159,211,1)"),
    opacity: new FormControl(70),
  });

  constructor(
    private dialogRef: MatDialogRef<EditLayerDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {
    const layer = data.layer as Viewerlayer;
    this.usedColors = data.usedColors ? data.usedColors : [];

    this.vectorLayer = !(layer.sourceName === "WMS" || layer.sourceName === "WMTS");

    if (this.vectorLayer && layer.sourceName !== "XYZ") {
      // Set proper validatos for vector type layers
      this.defaultForm.get("colorFill").setValidators([Validators.required, this.isValidColor()]);
      this.defaultForm.get("colorOutline").setValidators([Validators.required, this.isValidColor()]);

      if (layer.style) {
        const layerStyle = JSON.parse(layer.style) as ILayerStyleVector;
        if (layerStyle.defaultSetting) {
          layerStyle.defaultSetting.colorFillOpacity = Math.round(
            Number(layerStyle.defaultSetting.colorFillOpacity) * 100,
          ).toString();
          layerStyle.defaultSetting.colorOutlineOpacity = Math.round(
            Number(layerStyle.defaultSetting.colorOutlineOpacity) * 100,
          ).toString();
          this.defaultForm.patchValue({
            colorFill: layerStyle.defaultSetting.colorFill,
            colorOutline: layerStyle.defaultSetting.colorOutline,
            opacity: Number.parseFloat(layerStyle.defaultSetting.opacity),
          });
          this.colorFill = this.defaultForm.get("colorFill").value;
          this.colorOutline = this.defaultForm.get("colorOutline").value;
        }
      } else {
        this.colorFill = this.defaultForm.get("colorFill").value;
        this.colorOutline = this.defaultForm.get("colorOutline").value;
      }
      this.fields = layer?.customOverlayProperties?.layerProperties;
      this.addAdditionalStyle(layer.additionalStyles);
    } else if (!this.vectorLayer || layer.sourceName === "XYZ") {
      if (layer.opacity) {
        this.defaultForm.get("opacity").setValue(layer.opacity * 100);
      }
    }
  }

  ngOnInit() {}

  send() {
    if (!this.vectorLayer || this.data.layer.sourceName === "XYZ") {
      const opacity = this.defaultForm.get("opacity").value / 100;
      this.defaultForm.get("opacity").setValue(this.defaultForm.get("opacity").value / 100);
      const layerStyle: ILayerStyleVector = {
        defaultSetting: {
          colorFill: this.defaultForm.get("colorFill").value,
          colorOutline: this.defaultForm.get("colorOutline").value,
          opacity: this.defaultForm.get("opacity").value.toString(),
        },
      };
      this.dialogRef.close({
        style: layerStyle,
        status: 200,
        opacity: opacity,
      });
    } else {
      this.defaultForm.get("colorOutline").setValue(this.colorOutline);
      this.defaultForm.get("colorFill").setValue(this.colorFill);
      this.defaultForm.get("opacity").setValue(1);
      const layerStyle: ILayerStyleVector = {
        defaultSetting: {
          colorFill: this.defaultForm.get("colorFill").value,
          colorOutline: this.defaultForm.get("colorOutline").value,
          opacity: this.defaultForm.get("opacity").value.toString(),
        },
      };

      const additionalStyles = this.additionalStyles.filter((s) => s.field !== null);
      this.dialogRef.close({
        status: 200,
        style: layerStyle,
        opacity: 1,
        usedColors: this.usedColors,
        additionalStyles: additionalStyles.length > 0 ? additionalStyles : null,
      });
    }
  }

  close() {
    this.dialogRef.close();
  }

  isValidForm(): boolean {
    return this.defaultForm.valid;
  }

  isValidColor(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      return control.value.match(
        /(rgb\(((([01]?[0-9]?[0-9]|2[0-4][0-9]|25[0-5]),\s*){2}([01]?[0-9]?[0-9]|2[0-4][0-9]|25[0-5])\)))|(rgba\(((([01]?[0-9]?[0-9]|2[0-4][0-9]|25[0-5]),\s*){3}(0|1|1.0*|0?.\d+)\)))$/,
      ) === null
        ? { color: { value: control.value } }
        : null;
    };
  }

  addToUsedColors(color): void {
    if (this.usedColors.indexOf(color) === -1) {
      this.usedColors.push(color);
    }
  }

  onChangeFill(event: any) {
    this.colorFill = event;
  }

  onChangeOutline(event: any) {
    this.colorOutline = event;
  }

  updateField() {
    this.additionalStyles = [];
    this.addAdditionalStyle(null);
    this.infoBoxClosed = false;
  }

  addAdditionalStyle(additionalStyles) {
    if (additionalStyles) {
      this.additionalStyles = additionalStyles.map((style) => {
        return JSON.parse(JSON.stringify(style));
      });
      this.selectedField = this.additionalStyles[0].field;
    } else {
      this.additionalStyles.push({
        field: this.selectedField,
        filter: ["==", this.selectedField, ""],
        paint: {
          fillColor: "rgba(49,159,211,1)",
          outlineColor: "rgba(49,159,211,1)",
        },
        editFillColor: false,
        editOutlineColor: false,
      });
    }
  }

  removeStyleField(field) {
    this.additionalStyles = this.additionalStyles.filter((s) => s.field !== field);
    this.selectedField = null;
    if (this.additionalStyles.length == 0) {
      this.addAdditionalStyle(null);
    }

    this.infoBoxClosed = false;
  }

  removeStyleAttribute(style) {
    this.additionalStyles = this.additionalStyles.filter((s) => s != style);
    if (this.additionalStyles.length == 0) {
      this.addAdditionalStyle(null);
    }
  }

  getAttributesOfField(field): any[] {
    if (this.data.layer.source?.features)
      return Array.from(new Set(this.data.layer.source.features.features.map((f) => f.properties[field])));
  }

  isRgbaColor(color): boolean {
    const colorRegex =
      /(rgb\(((([01]?[0-9]?[0-9]|2[0-4][0-9]|25[0-5]),\s*){2}([01]?[0-9]?[0-9]|2[0-4][0-9]|25[0-5])\)))|(rgba\(((([01]?[0-9]?[0-9]|2[0-4][0-9]|25[0-5]),\s*){3}(0|1|1.0*|0?.\d+)\)))$/;
    return colorRegex.test(color);
  }

  cancelFillColor() {
    this.colorFill = this.defaultFillColor;
  }

  cancelOutlineColor() {
    this.colorOutline = this.defaultOutlineColor;
  }

  isAttributeSelected(attribute): boolean {
    return this.additionalStyles.some((as) => as.filter[2] === attribute);
  }

}
